import {HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';

import {Observable, of, Subject} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

import {
  PayrollInputDTO,
  PersonDataDTO,
  PersonDTO,
  PersonModelMetadataDTO,
  PersonPayrollLogDTO,
  SDC3Config,
  SdcLogDTO,
  SdcStatusDTO
} from './sdconnect.model';

import {REQUEST_OPTIONS} from '../../app.constants';
import {ApiService} from '../api';


@Injectable({
  providedIn: 'root'
})
export class SdconnectService {

  public sdcLogsRefreshed$: Subject<void> = new Subject();

  constructor(private api: ApiService) {
  }

  public static standardFields(metadata: PersonModelMetadataDTO): string[] {
    return metadata.fields
      .filter(field => field !== 'startDate' && field !== 'endDate' && field !== 'recordMetadata');
  }

  public static additionalFields(metadata: PersonModelMetadataDTO, data: PersonDataDTO[]): string[] {
    return Array.from(new Set([...data
      .flatMap(datum => Object.keys(datum.values))
      .filter(field => metadata.fields.indexOf(field) === -1)]));
  }

  public static entityIsDated(metadata: PersonModelMetadataDTO): boolean {
    return metadata.fields.indexOf('startDate') !== -1;
  }

  public static dataHasError(data: PersonDataDTO[]): boolean {
    return data.filter(datum => datum.status === 'FAILED').length > 0;
  }

  public getSdcLogs(contractId: string, perPage = 10, page = 0): Observable<{
    total: string | number,
    data: SdcLogDTO[]
  }> {

    return this.api.sdconnect
      .get(`/logs?per-page=${perPage}&page=${page}&search=contractId==${contractId}&order-by=-updateDate`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map((response: HttpResponse<SdcLogDTO[]>) => ({
          data: response.status === 204 ? [] : response.body,
          total: response.headers.get('x-total-count') || 0
        }))
      );
  }

  public forceRefreshStatus(contractId: string): Observable<void> {
    return this.api.sdconnect.post(`/admin/force-refresh-status/${contractId}`, {});
  }

  public forceSyncData(contractId: string, cdmEntity: string): Observable<void> {
    return this.api.sdconnect.post(`/admin/force-sync-data/${contractId}/${cdmEntity}`, {});
  }

  public forceSyncPersonToSDC3(contractId: string): Observable<void> {
    return this.api.sdconnect.post(`/admin/sdc3/contract/${contractId}/force-sync-person`, {});
  }

  public forceSyncPayrollToSDC3(contractId: string): Observable<void> {
    return this.api.sdconnect.post(`/admin/sdc3/contract/${contractId}/force-sync-payroll`, {});
  }

  public sendTestMessageToSDC3(contractId: string): Observable<void> {
    return this.api.sdconnect.post(`/admin/sdc3/contract/${contractId}/send-test-message`, {});
  }

  public getSdcLogsByEventIds(contractId: string, eventIds: string[]): Observable<SdcLogDTO[]> {
    const eventIdQuery = `eventId=in=(${eventIds.join(',')})`;
    const searchQuery = `contractId==${contractId};${eventIdQuery}`;
    return this.api.sdconnect
      .post(`/logs/list-by-post`, {search: searchQuery}, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map((response: HttpResponse<SdcLogDTO[]>) => response.body || [])
      );
  }

  public getSdcLog(sdcLogId: string): Observable<SdcLogDTO> {
    return this.api.sdconnect.get(`/logs?search=id==${sdcLogId}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(map((response: HttpResponse<SdcLogDTO[]>) => response.status === 204 ? null : response.body[0]));
  }

  public getPersonPayrollLogsBySdcLogId(path: string, sdcLogId: string, perPage = 10, page = 0): Observable<{
    total: string | number,
    data: PersonPayrollLogDTO[]
  }> {
    return this.getPersonPayrollLogs(path, `sdcLog.id==${sdcLogId}`, perPage, page);
  }

  public getPersonPayrollLogsByContractId(path: string, contractId: string, latest: boolean, perPage = 10, page = 0): Observable<{
    total: string | number,
    data: PersonPayrollLogDTO[]
  }> {
    let search = `contractId==${contractId}`;
    if (latest !== undefined) {
      search += `;latest==${latest}`;
    }
    return this.getPersonPayrollLogs(path, search, perPage, page);
  }

  public getPersonPayrollLogsBySdcLogSearch(path: string,
                                            search: string,
                                            sdcLogContractId: string,
                                            sdcLogCreateDate: string,
                                            sdcLogUpdateDate: string,
                                            sdcLogExternalReference: string,
                                            sdcLogPayGroup: string,
                                            sdcLogEventType: string,
                                            sdcLogEventStatus: string,
                                            sdcLogCorrelationId: string,
                                            sdcLogIncludedTypes: string[],
                                            sdcLogExcludedTypes: string[],
                                            perPage = 10, page = 0): Observable<{
    total: string | number,
    data: PersonPayrollLogDTO[]
  }> {
    const sdcLogOptions = {
      contractId: sdcLogContractId,
      employeeOtherId1: sdcLogExternalReference,
      payGroup: sdcLogPayGroup,
      status: sdcLogEventStatus,
      eventType: sdcLogEventType,
      correlationId: sdcLogCorrelationId
    };

    const sdcLogDateOptions = {
      creationDate: sdcLogCreateDate,
      updateDate: sdcLogUpdateDate,
    };

    const sdcLogSearch = this.buildSdcLogSearchQueryParams(sdcLogOptions, sdcLogDateOptions, sdcLogIncludedTypes, sdcLogExcludedTypes, 'sdcLog.');
    const computedSearch = [];
    if (search) {
      computedSearch.push(search);
    }
    if (sdcLogSearch) {
      computedSearch.push(sdcLogSearch);
    }

    return this.getPersonPayrollLogs(path, computedSearch.join(';'), perPage, page);
  }

  private getPersonPayrollLogs(path: string, search: string, perPage = 10, page = 0): Observable<{
    total: string | number,
    data: PersonPayrollLogDTO[]
  }> {
    const searchQueryParam = search ? `search=${search}&` : '';

    return this.api.sdconnect
      .get(`/admin/sdc3/${path}?per-page=${perPage}&page=${page}&${searchQueryParam}order-by=-updateDate`,
        REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map((response: HttpResponse<PersonPayrollLogDTO[]>) => ({
          data: response.status === 204 ? [] : response.body,
          total: response.headers.get('x-total-count') || 0
        })));
  }

  public getPerson(contractId: string): Observable<PersonDTO> {
    return this.api.sdconnect.get(`/sdc3/contract/${contractId}/person`);
  }

  public getPayroll(contractId: string): Observable<PayrollInputDTO> {
    return this.api.sdconnect.get(`/sdc3/contract/${contractId}/payroll`);
  }

  public isSdconnectEnabled(): boolean {
    return localStorage.getItem('gp_sdconnect_enabled') === 'true';
  }

  public getSdcStatus(contractId: number | string): Observable<SdcStatusDTO> {
    return this.api.sdconnect.silent.get(`/status/${contractId}`)
      .pipe(
        catchError(() => of({enabled: null, alerts: [], errors: [], v2: false, v3: false}))
      );
  }

  public getSdcLogsWithParams(contractId: string,
                              createDate: string,
                              updateDate: string,
                              externalReference: string,
                              payGroup: string,
                              eventType: string,
                              eventStatus: string,
                              correlationId: string,
                              includedTypes: string[],
                              excludedTypes: string[],
                              orderBy: string,
                              perPage: number = 10,
                              page: number = 0): Observable<{ total: string | number, data: SdcLogDTO[] }> {
    const options = {
      contractId: contractId,
      employeeOtherId1: externalReference,
      payGroup: payGroup,
      status: eventStatus,
      eventType: eventType,
      correlationId: correlationId
    };

    const dateOptions = {
      creationDate: createDate,
      updateDate: updateDate,
    };

    const search = this.buildSdcLogSearchQueryParams(options, dateOptions, includedTypes, excludedTypes);
    const searchQueryParam = search ? `search=${search}&` : '';
    const orderByQueryParam = orderBy ? `&order-by=${encodeURIComponent(orderBy)}` : '';

    return this.api.sdconnect
      .get(`/logs?${searchQueryParam}per-page=${perPage}&page=${page}${orderByQueryParam}`,
        REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map((response: HttpResponse<SdcLogDTO[]>) => ({
          data: response.status === 204 ? [] : response.body,
          total: response.headers.get('x-total-count') || 0
        }))
      );
  }

  private buildSdcLogSearchQueryParams(
    options: {
      contractId: string;
      employeeOtherId1: string;
      payGroup: string;
      status: string;
      eventType: string;
      correlationId: string
    },
    dateOptions: {
      creationDate: string;
      updateDate: string
    },
    includedTypes: string[],
    excludedTypes: string[],
    prefix = ''
  ): string {

    let queryParams = Object.entries(options)
      .filter(([, value]) => value)
      .map(([key, value]) => `${prefix}${key}==${value}`)
      .join(';');

    const dateQueryParams = Object.entries(dateOptions)
      .filter(([, value]) => value)
      .map(([key, value]) => this.buildDateQueryParams(`${prefix}${key}`, value))
      .join(';');

    let typeQueryParams = '';
    if (includedTypes && includedTypes.length > 0) {
      typeQueryParams = `${prefix}eventType=in=(${includedTypes.join(',')})`;
    }
    if (excludedTypes && excludedTypes.length > 0) {
      typeQueryParams = excludedTypes.map(type => `${prefix}eventType!=${type}`).join(';');
    }

    queryParams = [queryParams, typeQueryParams, dateQueryParams].filter(Boolean).join(';');

    return queryParams;
  }

  private buildDateQueryParams(field: string, date: string): string {
    if (!date) {
      return '';
    }

    const start = new Date(date);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);

    const startIso = start.toISOString().split('.')[0] + 'Z';
    const endIso = end.toISOString().split('.')[0] + 'Z';

    return `${field}=ge=${startIso};${field}=lt=${endIso}`;
  }

  public createOrUpdateSdc3Config(contractId: string, config: SDC3Config): Observable<unknown> {
    return this.api.sdconnect.put(`/admin/sdc3/contract/${contractId}/config`, config);
  }

  public getSdc3Config(contractId: string): Observable<SDC3Config> {
    return this.api.sdconnect.get(`/admin/sdc3/contract/${contractId}/config`);
  }
}

